import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import Edder from '../../../components/edd/FileEdder/FileEdder';
import '../Edd/Edd.css';
import './fedd.css';
import Header from '../../../components/Header/Header';
import FullPageLoader from '../../../components/edd/FullPageLoader/FullPageLoader';
import CancelSchedules from '../../../components/edd/CancelSchedules/CancelSchedules';
import {
  fetchFileDetails,
  reuploadFile,
  deleteFiles,
  downloadFiles,
  importFiles,
  validateFiles,
  scheduleImport,
  deployFiles,
  scheduleDeploy,
  cancelScheduledImport,
  cancelScheduledDeploy,
} from '../Edd/EddUtils';
import { EddFileInfo } from '../Edd/EddFileInfo';
import { EddErrorInfo } from '../../../components/edd/EddErrorInfo/EddErrorInfo';
import { EddCSVTable } from '../../../components/edd/EddCsvTable/EddCsvTable';
import EddSummary from '../../../components/edd/EddSummaryInfo/EddSummaryInfo';
import ConfirmModal from '../../../components/edd/ConfirmActions/ConfirmActions';
import ScheduleImport from '../../../components/edd/ScheduleImport/ScheduleImport';
import Error from '../../../components/edd/Error/Error';
import {
  deleteProceedHelper,
  handleFetchFileDetailsHelper,
  handleFileDataSuccessHelper,
  handleScheduleDeployHelper,
  importProceedHelper,
  reUploadProceedHelper,
  scheduleCancelInterfaceHelper,
  validateProceedHelper,
  handleScheduleImportHelper,
} from './FileDetailsPropDrillerUtils';

export default function FileDetails() {
  const history = useHistory();
  const params = useParams();
  const [fullPageLoading, setFullPageLoading] = useState(false);
  const [fullPageLoadingTitle, setFullPageLoadingTitle] = useState('');
  const [fileDetails, setFileDetails] = useState(undefined);
  const [confirmModalData, setConfirmModalData] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmModalTitle, setShowConfirmModalTitle] = useState('');
  const [showConfirmModalSubtitle, setShowConfirmModalSubtitle] = useState('');
  const [confirmModalOnClick, setConfirmModalOnClick] = useState(() => () => {});
  const [confirmModalBtnText, setConfirmModalBtnText] = useState('Submit');
  const [filesSelecting, setFilesSelecting] = useState(false);
  const [fileSelected, setFileSelected] = useState(null);
  const uploadFileRef = useRef(null);
  const [columns, setColumns] = useState([]);
  const [restructuredErrors, setRestructuredErrors] = useState({});
  const [cancelScheduleData, setCancelScheduleData] = useState([]);
  const [showCancelSchedule, setShowCancelSchedule] = useState(false);
  const [showScheduleImport, setShowScheduleImport] = useState(null);
  const [scheduleImportData, setScheduleImportData] = useState([]);
  const [scheduleDeployData, setScheduleDeployData] = useState([]);
  const [showScheduleDeploy, setShowScheduleDeploy] = useState(null);
  const scrollRef = useRef(null);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorDescription, setErrorDescription] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorAction] = useState(() => () => {});

  function handleFileDataSuccess(fileData) {
    handleFileDataSuccessHelper({
      fileData,
      setFileDetails,
      setFullPageLoading,
      setFullPageLoadingTitle,
      setRestructuredErrors,
      setColumns,
    });
  }

  useEffect(() => {
    handleFetchFileDetailsHelper({
      id: params.id,
      fetchFileDetails,
      handleFileDataSuccess,
      setFullPageLoading,
      setFullPageLoadingTitle,
      setErrorTitle,
      setErrorDescription,
      setShowError,
    });
  }, []);

  const importClicked = useCallback(() => {
    setShowConfirmModal(true);
    setShowConfirmModalTitle('Import File');
    setShowConfirmModalSubtitle("You're about to import");
    setConfirmModalData([fileDetails.fileName]);
    setConfirmModalOnClick(() => importProceed);
    setConfirmModalBtnText('Proceed');
  }, [fileDetails]);
  const deployClicked = useCallback(() => {
    setShowConfirmModal(true);
    setShowConfirmModalTitle('Deploy File');
    setShowConfirmModalSubtitle("You're about to deploy ");
    setConfirmModalData([fileDetails.fileName]);
    setConfirmModalOnClick(() => deployProceed);
    setConfirmModalBtnText('Deploy');
  }, [fileDetails]);
  const handleInputChange = useCallback(
    e => {
      setFileSelected(e.target.files[0]);
      const targetFile = e.target.files[0];

      uploadFileRef.current = targetFile;
      setFilesSelecting(false);
      setShowConfirmModal(true);
      setShowConfirmModalTitle('Re-Upload File');
      setShowConfirmModalSubtitle("You're about to re-upload");
      setConfirmModalData([`${e.target.files[0].name}`]);
      setConfirmModalOnClick(() => reUploadProceed);
      setConfirmModalBtnText('Proceed');
    },
    [fileDetails],
  );
  const reUploadProceed = useCallback(async () => {
    await reUploadProceedHelper({
      reuploadFile,
      uploadFileRef,
      fileDetails,
      setShowConfirmModal,
      setFullPageLoading,
      setFullPageLoadingTitle,
      setRestructuredErrors,
      setColumns,
      setFileDetails,
      setErrorTitle,
      setErrorDescription,
      setShowError,
    });
  }, [fileDetails, fileSelected]);
  const importProceed = useCallback(async () => {
    importProceedHelper({
      importFiles,
      fileDetails,
      setShowConfirmModal,
      setFullPageLoading,
      setFullPageLoadingTitle,
      setFileDetails,
      setErrorTitle,
      setErrorDescription,
      setShowError,
    });
  }, [fileDetails]);
  const deployProceed = useCallback(async () => {
    setShowConfirmModal(false);
    setFullPageLoading(true);
    setFullPageLoadingTitle('Deploying file');
    const {
      success,
      importedFiles = {},
      title = 'Something went wrong',
      description = 'Please try again later',
    } = await deployFiles([fileDetails]);

    if (success) {
      setFullPageLoading(false);
      setFileDetails(importedFiles[0]);
    } else {
      setFullPageLoading(false);
      setErrorTitle(title);
      setErrorDescription(description);
      setShowError(true);
      setShowConfirmModal(false);
    }
  }, [fileDetails]);
  const validateClicked = useCallback(() => {
    setShowConfirmModal(true);
    setShowConfirmModalTitle('Validate File');
    setShowConfirmModalSubtitle("You're about to re-validate ");
    setConfirmModalData([fileDetails.fileName]);
    setConfirmModalOnClick(() => validateProceed);
    setConfirmModalBtnText('Proceed');
  }, [fileDetails]);
  const validateProceed = useCallback(async () => {
    await validateProceedHelper({
      validateFiles,
      fileDetails,
      setShowConfirmModal,
      setFullPageLoading,
      setFullPageLoadingTitle,
      setFileDetails,
      setRestructuredErrors,
      setErrorTitle,
      setErrorDescription,
      setShowError,
    });
  }, [fileDetails]);
  const deleteClicked = useCallback(() => {
    setShowConfirmModal(true);
    setShowConfirmModalTitle('Delete Files');
    setShowConfirmModalSubtitle("You're about to delete");
    setConfirmModalData([fileDetails.fileName]);
    setConfirmModalOnClick(() => deleteProceed);
    setConfirmModalBtnText('Proceed');
  }, [fileDetails]);
  const deleteProceed = useCallback(async () => {
    await deleteProceedHelper({
      deleteFiles,
      fileDetails,
      setShowConfirmModal,
      setFullPageLoading,
      setFullPageLoadingTitle,
      setErrorTitle,
      setErrorDescription,
      setShowError,
      history,
    });
  }, [fileDetails]);
  const downloadClicked = useCallback(() => {
    setShowConfirmModal(true);
    setShowConfirmModalTitle('Download File');
    setShowConfirmModalSubtitle('');
    setConfirmModalData([fileDetails.fileName]);
    setConfirmModalOnClick(() => downloadProceed);
    setConfirmModalBtnText('Proceed');
  }, [fileDetails]);
  const downloadProceed = useCallback(async () => {
    setShowConfirmModal(false);
    setFullPageLoading(true);
    setFullPageLoadingTitle('Downloading file');
    const { success } = await downloadFiles([fileDetails]);

    if (success) {
      setFullPageLoading(false);
    } else {
      setFullPageLoading(false);
      setShowConfirmModal(true);
    }
  }, [fileDetails]);

  const errorViewClicked = () => {
    scrollRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScheduleImport = async (importDates, setDateArr) => {
    await handleScheduleImportHelper({
      scheduleImport,
      fileDetails,
      importDates,
      setShowScheduleImport,
      setFullPageLoading,
      setFullPageLoadingTitle,
      setFileDetails,
      setScheduleImportData,
      setDateArr,
      setErrorTitle,
      setErrorDescription,
      setShowError,
    });
  };

  const scheduleCancelInterface = async cancelInterface => {
    await scheduleCancelInterfaceHelper({
      cancelInterface,
      cancelScheduledDeploy,
      cancelScheduledImport,
      fileDetails,
      setShowCancelSchedule,
      setFullPageLoading,
      setFullPageLoadingTitle,
      setFileDetails,
      setErrorTitle,
      setErrorDescription,
      setShowError,
    });
  };

  const handleScheduleCanceled = async () => {
    setCancelScheduleData([
      {
        name: fileDetails.fileName,
        ...fileDetails,
        scheduledImportDate: fileDetails.scheduledImportDateString,
        scheduledDeployDate: fileDetails.scheduledDeploymentDateString,
      },
    ]);
    setShowCancelSchedule(true);
  };

  const scheduleClicked = async () => {
    setScheduleImportData([fileDetails.fileName]);
    setShowScheduleImport(true);
  };

  const handleScheduleDeploy = async (deployDates, setDateArr) => {
    await handleScheduleDeployHelper({
      scheduleDeploy,
      fileDetails,
      deployDates,
      setShowScheduleDeploy,
      setFullPageLoading,
      setFullPageLoadingTitle,
      setFileDetails,
      setScheduleDeployData,
      setDateArr,
      setErrorTitle,
      setErrorDescription,
      setShowError,
    });
  };

  const scheduleDeployClicked = async () => {
    setScheduleDeployData([fileDetails.fileName]);
    setShowScheduleDeploy(true);
  };

  return (
    <div className="custom-page tailwind-container items-center  pb-16 bg-[#EFF5F8] min-h-screen fed-con z-40">
      <Helmet title="edd details" />
      <Header
        overwrite={{
          id: { id: params.id, name: fileDetails ? fileDetails.fileName : params.id },
        }}
      />
      <Edder
        handleInputChange={() => {}}
        filesSelecting={() => {}}
        setFilesSelecting={() => {}}
        downloadClicked={downloadClicked}
      />
      <div className="items-center">
        <Error
          title={errorTitle}
          description={errorDescription}
          show={showError}
          setShowError={setShowError}
          errorAction={errorAction}
        />
        <FullPageLoader title={fullPageLoadingTitle} loading={fullPageLoading} />

        <ScheduleImport
          setShowScheduleImport={setShowScheduleImport}
          show={showScheduleImport}
          data={scheduleImportData}
          title="Schedule Import"
          onClick={() => {}}
          handleScheduleImport={handleScheduleImport}
          subtitle="When would you like to import these files?"
          scheduleImportButtonText="Set Import Date"
        />

        <CancelSchedules
          setShowCancelSchedule={setShowCancelSchedule}
          show={showCancelSchedule}
          data={cancelScheduleData}
          title="Cancel Schedules"
          onClick={() => {}}
          handleScheduleCancel={scheduleCancelInterface}
          subtitle="check the respective box to cancel a scheduled deployment or a scheduled import"
          scheduleImportButtonText="Cancel Schedule"
        />

        <ScheduleImport
          setShowScheduleImport={setShowScheduleDeploy}
          show={showScheduleDeploy}
          data={scheduleDeployData}
          title="Schedule Deploy"
          onClick={() => {}}
          handleScheduleImport={handleScheduleDeploy}
          subtitle="When would you like to deploy these files?"
          scheduleImportButtonText="Set Deploy Date"
        />
        <ConfirmModal
          setShowConfirmModal={setShowConfirmModal}
          show={showConfirmModal}
          data={confirmModalData}
          title={showConfirmModalTitle}
          onClick={confirmModalOnClick}
          subtitle={showConfirmModalSubtitle}
          confirmModalBtnText={confirmModalBtnText}
        />
        <div className="flex w-full  justify-between  ">
          {fileDetails !== undefined && (
            <>
              <EddFileInfo
                handleScheduleCanceled={handleScheduleCanceled}
                fileDetails={fileDetails}
                scheduleClicked={scheduleClicked}
                validateClicked={validateClicked}
                importClicked={importClicked}
                deployClicked={deployClicked}
                deleteClicked={deleteClicked}
                filesSelecting={filesSelecting}
                setFilesSelecting={setFilesSelecting}
                handleInputChange={handleInputChange}
                scheduleDeployClicked={scheduleDeployClicked}
                downloadClicked={downloadClicked}
              />
              {fileDetails.valid ? (
                <EddSummary fileDetails={fileDetails} />
              ) : (
                <EddErrorInfo fileDetails={fileDetails} errorViewClicked={errorViewClicked} />
              )}
            </>
          )}
        </div>

        {fileDetails !== undefined && <EddCSVTable columns={columns} errors={restructuredErrors} />}

        <div ref={scrollRef} />
      </div>
    </div>
  );
}
